import React, { useContext, useState, useMemo } from 'react';
import { DebugContext } from '../../app/providers/DebugContext';
import { FaTimes, FaTrash, FaCode, FaServer, FaClock, FaCheckCircle, FaExclamationCircle, FaArrowRight, FaFileExcel, FaFileCsv } from 'react-icons/fa';
import { FiActivity } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const DebugPanel = () => {
  const { isDebugMode, debugLogs, clearDebugLogs, toggleDebugMode } = useContext(DebugContext);
  const [selectedLog, setSelectedLog] = useState(null);
  const [filterType, setFilterType] = useState('ALL'); // ALL, REQUEST, ERROR

  // Hook'ları conditional return'den önce kullan (React hooks rules)
  const filteredLogs = useMemo(() => {
    if (filterType === 'ALL') return debugLogs;
    return debugLogs.filter(log => log.type === filterType);
  }, [debugLogs, filterType]);

  if (!isDebugMode) return null;

  const parseDebugDiagrun = (debugDiagrun) => {
    if (!debugDiagrun || typeof debugDiagrun !== 'object') return null;
    
    // Önce chain'leri ve step'leri topla
    const chains = [];
    const allSteps = [];
    
    Object.entries(debugDiagrun).forEach(([chainKey, chainValue]) => {
      if (chainValue && chainValue.steps && typeof chainValue.steps === 'object') {
        const stepsArray = Object.entries(chainValue.steps).map(([stepKey, stepValue]) => ({
          key: stepKey,
          id: stepValue.id || stepKey,
          name: stepValue.name || stepValue.id || stepKey,
          stepNumber: parseInt(stepKey) || 0
        }));
        
        chains.push({
          chainId: chainKey,
          diagrun: chainValue.diagrun || null,
          steps: stepsArray,
          firstStepNumber: stepsArray.length > 0 ? stepsArray[0].stepNumber : null,
          lastStepNumber: stepsArray.length > 0 ? stepsArray[stepsArray.length - 1].stepNumber : null
        });
        
        // Her step için tablo satırı oluştur
        stepsArray.forEach(step => {
          allSteps.push({
            chainId: chainKey,
            diagrunId: chainValue.diagrun?.id || '',
            diagrunName: chainValue.diagrun?.name || '',
            stepKey: step.key,
            stepId: step.id,
            stepName: step.name,
            stepNumber: step.stepNumber,
            includedChain: null // Sonra doldurulacak
          });
        });
      }
    });
    
    // Include ilişkilerini tespit et
    // Bir chain'in son step'i ile bir sonraki chain'in ilk step'i arasında 1 fark varsa include var
    chains.forEach((chain, chainIdx) => {
      if (chain.lastStepNumber !== null) {
        // Bu chain'in son step'ini bul
        const lastStepRow = allSteps.find(s => 
          s.chainId === chain.chainId && s.stepNumber === chain.lastStepNumber
        );
        
        if (lastStepRow) {
          // Bir sonraki chain'i bul (step numarasına göre)
          const nextChain = chains.find(c => 
            c.firstStepNumber !== null && 
            c.firstStepNumber === chain.lastStepNumber + 1
          );
          
          if (nextChain) {
            lastStepRow.includedChain = nextChain.chainId;
            lastStepRow.includedDiagrunName = nextChain.diagrun?.name || '';
          }
        }
      }
    });
    
    // Step numarasına göre sırala
    allSteps.sort((a, b) => a.stepNumber - b.stepNumber);
    
    return {
      chains,
      tableRows: allSteps
    };
  };

  const renderDebugDiagrunVisualization = (debugDiagrun) => {
    const parsed = parseDebugDiagrun(debugDiagrun);
    if (!parsed || !parsed.tableRows || parsed.tableRows.length === 0) return null;

    // CSV Export Fonksiyonu
    const exportToCSV = () => {
      const headers = ['#', 'Included Process ID', 'Diagrun ID', 'Diagrun Name', 'Step Key', 'Step Name', 'Included Process'];
      const csvData = parsed.tableRows.map((row, idx) => [
        idx + 1,
        row.chainId,
        row.diagrunId || '',
        row.diagrunName || '',
        row.stepKey,
        row.stepName,
        row.includedChain ? `Included Process ${row.includedChain}${row.includedDiagrunName ? ` (${row.includedDiagrunName})` : ''}` : '-'
      ]);
      
      const csvContent = [
        headers.join(','),
        ...csvData.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      ].join('\n');
      
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, `debug-diagrun-${new Date().toISOString().split('T')[0]}.csv`);
    };

    // XLSX Export Fonksiyonu
    const exportToXLSX = () => {
      const headers = ['#', 'Included Process ID', 'Diagrun ID', 'Diagrun Name', 'Step Key', 'Step Name', 'Included Process'];
      const xlsxData = parsed.tableRows.map((row, idx) => ({
        '#': idx + 1,
        'Included Process ID': row.chainId,
        'Diagrun ID': row.diagrunId || '',
        'Diagrun Name': row.diagrunName || '',
        'Step Key': row.stepKey,
        'Step Name': row.stepName,
        'Included Process': row.includedChain ? `Included Process ${row.includedChain}${row.includedDiagrunName ? ` (${row.includedDiagrunName})` : ''}` : '-'
      }));
      
      const ws = XLSX.utils.json_to_sheet(xlsxData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Debug Diagrun');
      
      // Kolon genişliklerini ayarla
      const colWidths = [
        { wch: 5 },   // #
        { wch: 20 },  // Included Process ID
        { wch: 15 },  // Diagrun ID
        { wch: 25 },  // Diagrun Name
        { wch: 10 },  // Step Key
        { wch: 30 },  // Step Name
        { wch: 30 }   // Included Process
      ];
      ws['!cols'] = colWidths;
      
      XLSX.writeFile(wb, `debug-diagrun-${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    return (
      <div className="mt-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <FaCode className="text-white text-sm" />
            </div>
            <h4 className="text-base font-bold text-slate-800">Debug Diagrun Analizi</h4>
            <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">
              {parsed.chains.length} included process • {parsed.tableRows.length} steps
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportToCSV}
              className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors flex items-center gap-1.5"
              title="CSV olarak indir"
            >
              <FaFileCsv className="text-xs" />
              CSV
            </button>
            <button
              onClick={exportToXLSX}
              className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-1.5"
              title="Excel olarak indir"
            >
              <FaFileExcel className="text-xs" />
              Excel
            </button>
          </div>
        </div>
        
        {/* Excel Benzeri Tablo */}
        <div className="bg-white border border-slate-300 rounded-lg overflow-hidden shadow-sm">
          <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
            <table className="w-full border-collapse" style={{ fontSize: '12px' }}>
              {/* Header - Sticky */}
              <thead className="sticky top-0 z-20">
                <tr className="bg-gradient-to-r from-slate-100 to-slate-200 border-b-2 border-slate-300">
                  <th className="border border-slate-300 px-3 py-2 text-left font-bold text-slate-700 bg-slate-200 sticky left-0 z-30 shadow-md">
                    #
                  </th>
                  <th className="border border-slate-300 px-3 py-2 text-left font-bold text-slate-700 bg-slate-200">
                    Included Process ID
                  </th>
                  <th className="border border-slate-300 px-3 py-2 text-left font-bold text-slate-700 bg-slate-200">
                    Diagrun ID
                  </th>
                  <th className="border border-slate-300 px-3 py-2 text-left font-bold text-slate-700 bg-slate-200">
                    Diagrun Name
                  </th>
                  <th className="border border-slate-300 px-3 py-2 text-left font-bold text-slate-700 bg-slate-200">
                    Step Key
                  </th>
                  <th className="border border-slate-300 px-3 py-2 text-left font-bold text-slate-700 min-w-[200px] bg-slate-200">
                    Step Name
                  </th>
                  <th className="border border-slate-300 px-3 py-2 text-left font-bold text-slate-700 min-w-[150px] bg-slate-200">
                    Included Process
                  </th>
                </tr>
              </thead>
              
              {/* Body */}
              <tbody>
                {parsed.tableRows.map((row, idx) => {
                  const isChainStart = idx === 0 || parsed.tableRows[idx - 1].chainId !== row.chainId;
                  const prevRow = idx > 0 ? parsed.tableRows[idx - 1] : null;
                  const isAfterInclude = prevRow && prevRow.includedChain;
                  
                  // Chain başlangıcı için özel stil
                  const rowBg = isChainStart 
                    ? 'bg-blue-50 border-l-4 border-l-blue-500' 
                    : isAfterInclude
                      ? 'bg-green-50 border-l-4 border-l-green-500'
                      : idx % 2 === 0 
                        ? 'bg-white' 
                        : 'bg-slate-50';
                  
                  return (
                    <tr 
                      key={`${row.chainId}-${row.stepKey}`}
                      className={`${rowBg} hover:bg-blue-100 transition-colors`}
                    >
                      <td className="border border-slate-300 px-3 py-2 font-mono text-slate-600 bg-slate-100 sticky left-0 z-10 font-semibold shadow-md">
                        {idx + 1}
                      </td>
                      <td className="border border-slate-300 px-3 py-2 font-mono text-slate-700 font-semibold">
                        {isChainStart && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded bg-blue-200 text-blue-800 text-xs font-bold mr-1">
                            →
                          </span>
                        )}
                        {row.chainId}
                      </td>
                      <td className="border border-slate-300 px-3 py-2 font-mono text-slate-600 text-xs">
                        {row.diagrunId || '-'}
                      </td>
                      <td className="border border-slate-300 px-3 py-2 text-slate-700 font-medium">
                        {row.diagrunName || '-'}
                      </td>
                      <td className="border border-slate-300 px-3 py-2 font-mono text-slate-600">
                        {row.stepKey}
                      </td>
                      <td className="border border-slate-300 px-3 py-2 text-slate-700">
                        {row.stepName}
                      </td>
                      <td className="border border-slate-300 px-3 py-2">
                        {row.includedChain ? (
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center px-2 py-1 rounded bg-green-100 text-green-700 font-semibold text-xs border border-green-300">
                              Included Process {row.includedChain}
                            </span>
                            {row.includedDiagrunName && (
                              <span className="text-xs text-slate-500 italic">
                                ({row.includedDiagrunName})
                              </span>
                            )}
                            <FaArrowRight className="text-green-600 text-xs" />
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-2">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-[98vw] h-[98vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-blue-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <FiActivity className="text-white text-lg" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">Debug Panel</h2>
              <p className="text-xs text-slate-500">{debugLogs.length} request logged</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={clearDebugLogs}
              className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-1.5"
            >
              <FaTrash className="text-xs" />
              Clear
            </button>
            <button
              onClick={toggleDebugMode}
              className="px-3 py-1.5 text-xs font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors flex items-center gap-1.5"
            >
              <FaTimes className="text-xs" />
              Close
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="px-6 py-3 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-slate-600">Filter:</span>
            {['ALL', 'REQUEST', 'ERROR'].map(type => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                  filterType === type
                    ? 'bg-blue-500 text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-100'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Log List */}
          <div className="w-1/3 border-r border-slate-200 overflow-y-auto bg-slate-50">
            <div className="p-2 space-y-2">
              {filteredLogs.length === 0 ? (
                <div className="text-center py-8 text-slate-400 text-sm">
                  No logs yet
                </div>
              ) : (
                filteredLogs.map((log) => (
                  <div
                    key={log.id}
                    onClick={() => setSelectedLog(log)}
                    className={`p-3 rounded-lg cursor-pointer transition-all ${
                      selectedLog?.id === log.id
                        ? 'bg-blue-500 text-white shadow-md'
                        : 'bg-white hover:bg-blue-50 border border-slate-200'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {log.type === 'ERROR' ? (
                            <FaExclamationCircle className={`text-xs ${selectedLog?.id === log.id ? 'text-red-200' : 'text-red-500'}`} />
                          ) : (
                            <FaCheckCircle className={`text-xs ${selectedLog?.id === log.id ? 'text-green-200' : 'text-green-500'}`} />
                          )}
                          <span className={`text-xs font-mono font-semibold truncate ${selectedLog?.id === log.id ? 'text-white' : 'text-slate-700'}`}>
                            {log.processId}
                          </span>
                        </div>
                        <div className={`text-[10px] truncate ${selectedLog?.id === log.id ? 'text-blue-100' : 'text-slate-500'}`}>
                          {log.callingFunction}
                        </div>
                        <div className={`text-[10px] mt-1 flex items-center gap-1 ${selectedLog?.id === log.id ? 'text-blue-100' : 'text-slate-400'}`}>
                          <FaClock className="text-[9px]" />
                          {log.formattedTime} • {log.duration || 'N/A'}
                        </div>
                        {log.hasDebugDiagrun && (
                          <div className={`text-[10px] mt-1 flex items-center gap-1 ${selectedLog?.id === log.id ? 'text-yellow-200' : 'text-yellow-600'}`}>
                            <FaCode className="text-[9px]" />
                            Debug Data
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Detail View */}
          <div className="flex-1 overflow-y-auto bg-white p-6">
            {selectedLog ? (
              <div className="space-y-4">
                {/* Debug Diagrun Visualization - En Üstte */}
                {selectedLog.debugDiagrun && renderDebugDiagrunVisualization(selectedLog.debugDiagrun)}

                {/* Header */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-lg font-bold text-slate-800">Request Details</h3>
                      <p className="text-xs text-slate-500 mt-1">{selectedLog.formattedTime}</p>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      selectedLog.type === 'ERROR' 
                        ? 'bg-red-100 text-red-700' 
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {selectedLog.type}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Process ID:</span>
                      <span className="ml-2 font-mono font-semibold text-slate-800">{selectedLog.processId}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Duration:</span>
                      <span className="ml-2 font-semibold text-slate-800">{selectedLog.duration || 'N/A'}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-slate-500">Source Function:</span>
                      <span className="ml-2 font-mono text-slate-800">{selectedLog.callingFunction}</span>
                    </div>
                  </div>
                </div>

                {/* Request Body */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <FaArrowRight className="text-blue-500" />
                    Request Body
                  </h4>
                  <pre className="text-xs bg-white p-3 rounded border border-slate-200 overflow-x-auto">
                    {JSON.stringify(selectedLog.requestBody, null, 2)}
                  </pre>
                </div>

                {/* Response Data */}
                {selectedLog.responseData && (
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                      <FaServer className="text-green-500" />
                      Response Data
                    </h4>
                    <pre className="text-xs bg-white p-3 rounded border border-slate-200 overflow-x-auto max-h-64">
                      {JSON.stringify(selectedLog.responseData, null, 2)}
                    </pre>
                  </div>
                )}

                {/* Error */}
                {selectedLog.error && (
                  <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                    <h4 className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-2">
                      <FaExclamationCircle />
                      Error
                    </h4>
                    <p className="text-sm text-red-600">{selectedLog.error}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-slate-400">
                <div className="text-center">
                  <FiActivity className="text-4xl mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Select a log to view details</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DebugPanel;

